import axiosInstance from "api/axiosInstance";
import getUserInfo from "api/login/getUserInfo";

// Create a new meeting
export async function createMeeting(roomName, mailListId = null) {
  try {
    const response = await axiosInstance.post(
      "/api3.2/meet/createMeeting",
      { roomName, mailListId },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("[MeetAPI] Meeting created:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to create meeting:", error);
    throw error;
  }
}

// Delete a meeting
export async function deleteMeeting(recordingId) {
  if (!recordingId) {
    throw new Error("Recording ID is required!");
  }

  try {
    const response = await axiosInstance.delete(`/api3.2/meet/deleteMeeting/${recordingId}`);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to delete meeting:", error);
    throw error;
  }
}

// Download a meeting
export async function downloadMeeting(recordingId) {
  if (!recordingId) {
    throw new Error("Recording ID is required!");
  }

  try {
    const response = await axiosInstance.get(`/api3.2/meet/downloadMeeting/${recordingId}`);
    console.log("[MeetAPI] Download link fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch download link:", error);
    throw error;
  }
}

// Download a meeting transcript
export async function downloadTranscript(recordingId) {
  if (!recordingId) {
    throw new Error("Recording ID is required!");
  }

  try {
    const response = await axiosInstance.get(`/api3.2/meet/downloadTranscript/${recordingId}`, {
      timeout: 120000,
    });
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch transcript link:", error);
    throw error;
  }
}

// Fetch all meetings
export async function fetchMeeting() {
  try {
    const response = await axiosInstance.get("/api3.2/meet/fetchMeeting");
    console.log("[MeetAPI] Meetings fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch meetings:", error);
    throw error;
  }
}

// Create a scheduled meeting (backend creates Whereby room + DB row atomically)
export async function createScheduledMeeting(meetingData) {
  let employeeId = null;
  try {
    let userInfo = await getUserInfo();
    employeeId = userInfo.data.EmployeeId;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch user info:", error);
    throw new Error("Failed to fetch user information for meeting creation");
  }

  const meetingDataWithEmployeeId = {
    ...meetingData,
    meetingCreatorEmployeeId: employeeId,
  };

  try {
    const response = await axiosInstance.post(
      "/api3.2/meet/createScheduledMeeting",
      meetingDataWithEmployeeId,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    console.log("[MeetAPI] Scheduled meeting created:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to create scheduled meeting:", error);
    throw error;
  }
}

// Fetch all scheduled meetings
export async function fetchScheduledMeetings() {
  let employeeId = null;
  try {
    let userInfo = await getUserInfo();
    employeeId = userInfo.data.EmployeeId;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch user info:", error);
    throw new Error("Failed to fetch user information for meeting retrieval");
  }

  try {
    const response = await axiosInstance.get("/api3.2/meet/fetchScheduledMeetings", {
      params: { employeeId },
    });
    console.log("[MeetAPI] Scheduled meetings fetched:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch scheduled meetings:", error);
    throw error;
  }
}

// Update a scheduled meeting
export async function updateScheduledMeeting(meetingId, updateData) {
  let employeeId = null;
  try {
    let userInfo = await getUserInfo();
    employeeId = userInfo.data.EmployeeId;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch user info:", error);
    throw new Error("Failed to fetch user information for meeting update");
  }

  const updateDataWithEmployeeId = {
    ...updateData,
    employeeId: employeeId,
  };

  try {
    const response = await axiosInstance.put(
      `/api3.2/meet/updateScheduledMeeting/${meetingId}`,
      updateDataWithEmployeeId,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );
    console.log("[MeetAPI] Scheduled meeting updated:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to update scheduled meeting:", error);
    throw error;
  }
}

// Delete a scheduled meeting
export async function deleteScheduledMeeting(meetingId) {
  let employeeId = null;
  try {
    let userInfo = await getUserInfo();
    employeeId = userInfo.data.EmployeeId;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch user info:", error);
    throw new Error("Failed to fetch user information for meeting deletion");
  }

  try {
    const response = await axiosInstance.delete(`/api3.2/meet/deleteScheduledMeeting/${meetingId}`, {
      data: { employeeId },
      headers: {
        "Content-Type": "application/json",
      },
    });
    console.log("[MeetAPI] Scheduled meeting deleted:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to delete scheduled meeting:", error);
    throw error;
  }
}

// Turn an agenda document into plain text, at attach time.
//
// This is the only multipart request in this module - every other function here sends JSON. The
// file goes no further than this call: the summary POST below carries the returned TEXT, never
// file bytes.
//
// The route literal lives here and nowhere else, so renaming the endpoint is a one-line edit.
export async function extractAgenda(file) {
  if (!file) {
    throw new Error("Agenda file is required!");
  }

  const formData = new FormData();
  formData.append("file", file);

  try {
    // No Content-Type header on purpose. The browser sets multipart/form-data AND the boundary
    // that goes with it; hand-writing the header omits the boundary and the server cannot parse
    // the body.
    const response = await axiosInstance.post("/api3.2/meet/extractAgenda", formData, {
      // Long enough for a 10 MB PDF to be parsed server-side, short enough that a dead connection
      // does not leave the panel spinning. The shared axiosInstance sets no default timeout.
      timeout: 60000,
    });
    console.log("[MeetAPI] Agenda extracted:", response.data?.thinExtraction);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to extract agenda:", error);
    throw error;
  }
}

// The ONE function in this module that builds a summary POST. Both exported entry points below
// delegate to it, so there is exactly one place a request body is assembled - and therefore
// exactly one place the replace flag can reach the wire.
//
// `options` may carry `agendaText` and `instructions`. Both keys are OMITTED when the caller has
// nothing for them, rather than sent as empty strings, so that "absent" and "empty" cannot
// diverge on the server. A caller that passes neither sends the same `{}` body this endpoint has
// always been sent.
//
// The third argument is deliberately positional and deliberately not part of `options`: it can
// only be supplied by a caller that means to supply it, and only one exported function does.
async function postRecordingSummary(recordingId, options, regenerate) {
  if (!recordingId) {
    throw new Error("Recording ID is required!");
  }

  const body = {};
  if (options.agendaText) {
    body.agendaText = options.agendaText;
  }
  if (options.instructions) {
    body.instructions = options.instructions;
  }
  // THE ONLY PLACE IN THIS REPOSITORY THAT PUTS THIS KEY ON THE WIRE.
  // Money-safety rule: the flag may exist only on the user-click path. The frontend's automatic
  // re-POST for a finished job shares this endpoint, so a flag set anywhere reachable from that
  // path would rewrite the report on every page load, at roughly $3 an hour, and the backend's
  // token-usage log line is the only detector. Do not move this line, do not widen it to read
  // from `options`, and do not add a second setter.
  if (regenerate) {
    body.regenerate = true;
  }

  try {
    const response = await axiosInstance.post(
      `/api3.2/meet/recordings/${recordingId}/summary`,
      body,
      {
        headers: {
          "Content-Type": "application/json",
        },
        // 20 s is a dead-connection bound, not a wait for the report: the success branches
        // (cached 200, 202 processing, 202 started) answer in milliseconds against
        // the async backend, which rollout ordering guarantees is deployed ahead of this frontend.
        timeout: 20000,
      }
    );
    console.log("[MeetAPI] Recording summary generated:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to generate recording summary:", error);
    throw error;
  }
}

// Generate AI summary from a recording. Never asks the backend to replace a stored report, so a
// recording that already has one answers from the cache exactly as it does today. This is the
// function every automatic path uses.
export async function generateRecordingSummary(recordingId, options = {}) {
  return postRecordingSummary(recordingId, options, false);
}

// Generate AI summary from a recording, replacing a stored report if there is one.
//
// This entry point exists so that the replacement intent has exactly one door, and that door is
// opened by a user clicking through a confirmation dialog. It must never be called from a poll
// tick, a retry, a re-attach, or any other automatic path.
export async function regenerateRecordingSummary(recordingId, options = {}) {
  return postRecordingSummary(recordingId, options, true);
}

// Check the status of a background AI summary job
export async function getRecordingSummaryStatus(recordingId) {
  if (!recordingId) {
    throw new Error("Recording ID is required!");
  }

  try {
    const response = await axiosInstance.get(`/api3.2/meet/recordings/${recordingId}/summary/status`, {
      // The shared axiosInstance (src/api/axiosInstance.ts) sets no default timeout, so this is the
      // request's only bound: without it, a stalled backend leaves one permanently-dangling request
      // and the in-flight guard then freezes the poll loop. 10 s deliberately equals the poll
      // period, so two ticks of one cycle cannot overlap by construction; close/reopen overlap is
      // pollGenerationRef's job (MeetingHistory.tsx), not this timeout's.
      timeout: 10000,
    });
    // Deliberately no success console.log: this is polled every 10 s for up to ~40 minutes
    // (SUMMARY_POLL_INTERVAL_MS / SUMMARY_POLL_CEILING_MS in MeetingHistory.tsx).
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to fetch summary status:", error);
    throw error;
  }
}

// Export AI report as PDF
export async function exportReportPdf(recordingId, meetingName, meetingDate) {
  if (!recordingId) {
    throw new Error("Recording ID is required!");
  }

  try {
    const response = await axiosInstance.post(
      `/api3.2/meet/recordings/${recordingId}/export-pdf`,
      { meetingName, meetingDate },
      {
        headers: {
          "Content-Type": "application/json",
        },
        timeout: 60000,
      }
    );
    console.log("[MeetAPI] PDF exported:", response.data);
    return response.data;
  } catch (error) {
    console.error("[MeetAPI] Failed to export PDF:", error);
    throw error;
  }
}
